<template>
  <Tabs
    v-model:value="activeTab"
    scrollable
    v-bind="$attrs"
    class="cn-tabs-new"
  >
    <template #previcon>
      <UiIcon name="chevron-left" />
    </template>
    <template #nexticon>
      <UiIcon name="chevron-right" />
    </template>

    <TabList>
      <Tab
        v-for="(tab, index) in tabs"
        :key="index"
        :value="index"
        @click="toggle(index)"
      >
        <slot name="tab" :tab="tab" :index="index" />
      </Tab>
    </TabList>
    <TabPanels>
      <TabPanel v-for="(tab, index) in tabs" :key="index" :value="index">
        <slot name="panel" :tab="tab" :index="index" />
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  tabs: unknown[];
  modelValue?: number | null;
}

// PrimeVue always keeps one tab selected; `null` (nothing open) is expressed by
// pointing `value` at an index no panel owns
const NO_TAB = -1;

const props = withDefaults(defineProps<Props>(), { modelValue: null });
const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void;
}>();

const activeTab = computed({
  get: () => props.modelValue ?? NO_TAB,
  set: (value: number) =>
    emit('update:modelValue', value === NO_TAB ? null : value),
});

// Clicking the open tab closes it — PrimeVue's own handler stays silent when the
// value does not change, so the toggle has to live here
const toggle = (index: number) => {
  if (activeTab.value === index) activeTab.value = NO_TAB;
};
</script>

<style lang="scss">
.cn-tabs-new {
  --ab-canyon-tabs-color-text: var(--ab-canyon-color-dark);
}
</style>

<style lang="scss" scoped>
.p-tabs {
  --tabs_color: var(--ab-canyon-tabs-color-text);
  --tabs__header_color: var(--tabs_color);
  --tabs__panel_color: var(--tabs_color);
  --tabs__header_highlight-color: var(--tabs_color);

  :deep(.p-tablist) {
    color: var(--tabs__header_color);
    align-items: center;
    display: flex;
    position: relative;
  }

  :deep(.p-tablist-content) {
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    margin: 0 -10px;
    padding: 0 10px;
    flex-grow: 1;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  :deep(.p-tablist-tab-list) {
    display: flex;
    position: relative;
  }

  :deep(.p-tablist-active-bar) {
    display: none;
  }

  :deep(.p-tab) {
    color: var(--tabs__header_color);
    flex-shrink: 0;
    outline: none;

    &:focus,
    &:hover,
    &[data-p-active='true'] {
      color: var(--tabs__header_highlight-color);
    }

    &:not(:last-child) {
      margin-right: 30px;
    }
  }

  :deep(.p-tablist-nav-button) {
    color: var(--tabs__buttons_color, currentColor);
    font-size: 20px;
    flex-shrink: 0;
  }
  :deep(.p-tablist-prev-button) {
    margin-right: 28px;
  }
  :deep(.p-tablist-next-button) {
    margin-left: 28px;
  }

  // Edge fades: PrimeVue only renders a nav button while there is more to
  // scroll that way, so the buttons double as the "is scrolled" signal
  :deep(.p-tablist-content) {
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 10px;
      z-index: 1;
      opacity: 0;
    }
    &::before {
      left: 0;
      background: linear-gradient(
        90deg,
        var(--tabs_bg-color, var(--tabs_color)),
        transparent
      );
    }
    &::after {
      right: 0;
      background: linear-gradient(
        -90deg,
        var(--tabs_bg-color, var(--tabs_color)),
        transparent
      );
    }
  }
  :deep(.p-tablist-prev-button + .p-tablist-content::before) {
    opacity: 1;
  }
  :deep(.p-tablist-content:has(+ .p-tablist-next-button)::after) {
    opacity: 1;
  }

  :deep(.p-tabpanel) {
    color: var(--tabs__panel_color);
    outline: none;
  }
}
</style>
